import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Search, Filter, Eye, Edit, Trash2, Plus, Package, MapPin, Upload, ShoppingCart } from 'lucide-react';
import Select, { StylesConfig, OptionProps, components, CSSObjectWithLabel, ControlProps, OptionProps as ReactSelectOptionProps } from 'react-select';
import Button from '../components/UI/Button';
import Table from '../components/UI/Table';
import Modal from '../components/UI/Modal';
import toast from 'react-hot-toast';
import apiService from '../services/api';
import { Bundle, Tour, BundleFormData } from '../types';
import { useForm } from 'react-hook-form';
import { extractTourText } from '../utils/tourUtils';

type OptionType = {
  value: number | 'add_new';
  label: string;
};

const Bundles: React.FC = () => {
  const [bundles, setBundles] = useState<Bundle[]>([]);
  const [tours, setTours] = useState<Tour[]>([]);
  const [availableTours, setAvailableTours] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterName, setFilterName] = useState('');
  const [filterNameInput, setFilterNameInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalBundles, setTotalBundles] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const rowsPerPage = 10;
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedBundle, setSelectedBundle] = useState<Bundle | null>(null);
  const [sortColumn, setSortColumn] = useState<keyof Bundle>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedTourIds, setSelectedTourIds] = useState<number[]>([]);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [removeExistingImage, setRemoveExistingImage] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Tour infinite scroll pagination
  const [tourPage, setTourPage] = useState(1);
  const [tourTotalPages, setTourTotalPages] = useState(1);
  const [isLoadingMoreTours, setIsLoadingMoreTours] = useState(false);
  const tourListRef = useRef<HTMLDivElement>(null);

  const [countries, setCountries] = useState<Array<{ id: number; name: string; code?: string }>>([]);
  const [places, setPlaces] = useState<Array<{ id: number; name: string; countryId: number }>>([]);
  const [selectedCountryId, setSelectedCountryId] = useState<number | ''>('');
  const [selectedPlaceId, setSelectedPlaceId] = useState<number | ''>('');
  const [showAddCountryModal, setShowAddCountryModal] = useState(false);
  const [showAddPlaceModal, setShowAddPlaceModal] = useState(false);
  const [newCountryName, setNewCountryName] = useState('');
  const [newPlaceName, setNewPlaceName] = useState('');
  const [isCreatingCountry, setIsCreatingCountry] = useState(false);
  const [isCreatingPlace, setIsCreatingPlace] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<BundleFormData>();

  // Debounced filter effect
  useEffect(() => {
    const nameTimer = setTimeout(() => {
      setFilterName(filterNameInput);
      setCurrentPage(1); // Reset to first page when filter changes
    }, 500); // 500ms delay

    return () => clearTimeout(nameTimer);
  }, [filterNameInput]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const data = await apiService.getCountries();
        setCountries(data);
      } catch (error) {
        toast.error('Failed to load countries');
      }
    };
    fetchCountries();
  }, []);

  useEffect(() => {
    const fetchPlaces = async () => {
      if (selectedCountryId) {
        try {
          const data = await apiService.getPlaces(selectedCountryId as number);
          setPlaces(data);
        } catch (error) {
          toast.error('Failed to load places');
        }
      } else {
        setPlaces([]);
        setSelectedPlaceId('');
      }
    };
    fetchPlaces();
  }, [selectedCountryId]);

  useEffect(() => {
    if (selectedBundle) {
      setSelectedCountryId(selectedBundle.countryId ?? '');
      setSelectedPlaceId(selectedBundle.placeId ?? '');
    }
  }, [selectedBundle]);

  // Fetch bundles, tours, and available tours
  const fetchData = async (page: number = currentPage, filter: string = filterName) => {
    setIsLoading(true);
    setError(null);
    try {
      const flavourId = apiService.getFlavourId();
      if (!flavourId) {
        setBundles([]);
        setTotalBundles(0);
        setTotalPages(1);
        setTours([]);
        setAvailableTours([]);
        setIsLoading(false);
        return;
      }

      const [bundlesResponse, toursResponse] = await Promise.all([
        apiService.getBundles(page, rowsPerPage, filter),
        apiService.getTours(undefined, undefined, 1, 12)
      ]);

      // Handle paginated bundles response
      if (bundlesResponse && bundlesResponse.bundles) {
        setBundles(bundlesResponse.bundles);
        setTotalBundles(bundlesResponse.total);
        setTotalPages(bundlesResponse.totalPages);
        // Don't update currentPage here - it's controlled by state
      } else {
        // Fallback for backward compatibility
        const bundlesArray = Array.isArray(bundlesResponse) ? bundlesResponse : [];
        setBundles(bundlesArray);
        setTotalBundles(bundlesArray.length);
        setTotalPages(1);
      }

      // Handle tours response with pagination reset
      const tours = toursResponse?.data || [];
      const totalTourPages = toursResponse?.pagination?.totalPages ?? 1;
      setTours(tours);
      setAvailableTours(tours);
      setTourPage(1);
      setTourTotalPages(totalTourPages);
    } catch (error) {
      setError('Failed to load data');
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  // Initial fetch and refetch when page or filter changes
  useEffect(() => {
    fetchData(currentPage, filterName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filterName]);

  // Fetch next page of tours and append to the list
  const fetchMoreTours = useCallback(async () => {
    const nextPage = tourPage + 1;
    if (isLoadingMoreTours || nextPage > tourTotalPages) return;
    setIsLoadingMoreTours(true);
    try {
      const response = await apiService.getTours(undefined, undefined, nextPage, 12);
      const moreTours = response?.data || [];
      setAvailableTours(prev => [...prev, ...moreTours]);
      setTourPage(nextPage);
    } catch (err) {
      // ignore tour load failure
    } finally {
      setIsLoadingMoreTours(false);
    }
  }, [tourPage, tourTotalPages, isLoadingMoreTours]);

  // Infinite scroll handler for tour list
  const handleTourListScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 80) {
      fetchMoreTours();
    }
  }, [fetchMoreTours]);

  // Sort bundles (client-side sorting for displayed data)
  const sortedBundles = [...bundles].sort((a, b) => {
    const aValue = a[sortColumn];
    const bValue = b[sortColumn];
    
    if (aValue === undefined || bValue === undefined) return 0;
    
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    
    return 0;
  });

  const handleSort = (column: keyof Bundle) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleView = (bundle: Bundle) => {
    setSelectedBundle(bundle);
    setIsViewModalOpen(true);
  };

  const handleEdit = (bundle: Bundle) => {
    setSelectedBundle(bundle);
    
    // Populate form with bundle data (extract text if it's multilingual)
    setValue('name', typeof bundle.name === 'string' ? bundle.name : extractTourText(bundle.name));
    setValue('description', bundle.description ? (typeof bundle.description === 'string' ? bundle.description : extractTourText(bundle.description)) : '');
    setValue('place', bundle.place || '');
    if (bundle.countryId) setSelectedCountryId(bundle.countryId);
    if (bundle.placeId) setSelectedPlaceId(bundle.placeId);
    setValue('productId', bundle.productId || '');
    setValue('bokunId', bundle.bokunId || '');
    setValue('bundleEmailTemplate', bundle.bundleEmailTemplate || '');
    setValue('sellingPrice', bundle.sellingPrice);
    setValue('retailPrice', bundle.retailPrice);
    setValue('status', bundle.status);
    
    // Set selected tours
    const tourIds = bundle.bundelDetails?.map(detail => detail.tourId) || [];
    setSelectedTourIds(tourIds);
    setValue('tourIds', tourIds);
    
    // Clear thumbnail file so existing image shows
    setThumbnailFile(null);
    setRemoveExistingImage(false);
    
    setIsEditModalOpen(true);
  };

  const countryOptions = useMemo<OptionType[]>(() => {
    const options: OptionType[] = countries.map(country => ({
      value: country.id,
      label: country.name,
    }));
    options.push({ value: 'add_new', label: '+ Add New Country' });
    return options;
  }, [countries]);

  const placeOptions = useMemo<OptionType[]>(() => {
    const options: OptionType[] = places.map(place => ({
      value: place.id,
      label: place.name,
    }));
    if (selectedCountryId) {
      options.push({ value: 'add_new', label: '+ Add New Place' });
    }
    return options;
  }, [places, selectedCountryId]);

  const Option = (props: OptionProps<OptionType>) => {
    const isAddNew = props.data.value === 'add_new';
    return (
      <components.Option
        {...props}
        className={isAddNew ? 'text-blue-600 font-semibold' : ''}
      >
        {props.children}
      </components.Option>
    );
  };

  const selectStyles: StylesConfig<OptionType, false> = {
    control: (base: CSSObjectWithLabel, state: ControlProps<OptionType, false>) => ({
      ...base,
      borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
      boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
      '&:hover': {
        borderColor: state.isFocused ? '#3b82f6' : '#9ca3af',
      },
    }),
    option: (base: CSSObjectWithLabel, state: ReactSelectOptionProps<OptionType, false>) => ({
      ...base,
      backgroundColor: state.isSelected
        ? '#3b82f6'
        : state.isFocused
        ? '#eff6ff'
        : 'white',
      color: state.isSelected ? 'white' : state.data?.value === 'add_new' ? '#2563eb' : '#111827',
      fontWeight: state.data?.value === 'add_new' ? '600' : 'normal',
    }),
  };

  const handleCountryChange = (selectedOption: OptionType | null) => {
    if (!selectedOption) {
      setSelectedCountryId('');
      setSelectedPlaceId('');
      setValue('place', '');
      setValue('countryId', undefined);
      setValue('placeId', undefined);
      return;
    }
    if (selectedOption.value === 'add_new') {
      setShowAddCountryModal(true);
    } else {
      const countryId = selectedOption.value as number;
      setSelectedCountryId(countryId);
      setSelectedPlaceId('');
      setValue('place', '');
      setValue('countryId', countryId);
      setValue('placeId', undefined);
    }
  };

  const handlePlaceChange = (selectedOption: OptionType | null) => {
    if (!selectedOption) {
      setSelectedPlaceId('');
      setValue('place', '');
      setValue('placeId', undefined);
      return;
    }
    if (selectedOption.value === 'add_new') {
      if (!selectedCountryId) {
        toast.error('Please select a country first');
        return;
      }
      setShowAddPlaceModal(true);
    } else {
      const placeId = selectedOption.value as number;
      const selectedPlace = places.find(p => p.id === placeId);
      setSelectedPlaceId(placeId);
      setValue('place', selectedPlace ? selectedPlace.name : '');
      setValue('placeId', placeId);
    }
  };

  const handleCreateCountry = async () => {
    if (!newCountryName.trim()) {
      toast.error('Please enter a country name');
      return;
    }
    setIsCreatingCountry(true);
    try {
      const newCountry = await apiService.createCountry({ name: newCountryName.trim() });
      setCountries(prev => [...prev, newCountry].sort((a, b) => a.name.localeCompare(b.name)));
      setSelectedCountryId(newCountry.id);
      setValue('countryId', newCountry.id);
      setNewCountryName('');
      setShowAddCountryModal(false);
      toast.success('Country created successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to create country');
    } finally {
      setIsCreatingCountry(false);
    }
  };

  const handleCreatePlace = async () => {
    if (!newPlaceName.trim()) {
      toast.error('Please enter a place name');
      return;
    }
    if (!selectedCountryId) {
      toast.error('Please select a country first');
      return;
    }
    setIsCreatingPlace(true);
    try {
      const newPlace = await apiService.createPlace({
        name: newPlaceName.trim(),
        countryId: selectedCountryId as number,
      });
      setPlaces(prev => [...prev, newPlace].sort((a, b) => a.name.localeCompare(b.name)));
      setSelectedPlaceId(newPlace.id);
      setValue('placeId', newPlace.id);
      setValue('place', newPlace.name);
      setNewPlaceName('');
      setShowAddPlaceModal(false);
      toast.success('Place created successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to create place');
    } finally {
      setIsCreatingPlace(false);
    }
  };

  const handleAdd = () => {
    reset();
    setSelectedTourIds([]);
    setSelectedCountryId('');
    setSelectedPlaceId('');
    setIsAddModalOpen(true);
  };

  const onSubmitAdd = async (data: BundleFormData) => {
    // Validate that an image is uploaded for new bundles
    if (!thumbnailFile) {
      toast.error('Please upload a thumbnail image for the bundle');
      return;
    }

    // Validate retail price must be greater than selling price when both are provided
    const retailVal = data.retailPrice != null && String(data.retailPrice).trim() !== '' ? parseFloat(String(data.retailPrice)) : NaN;
    const sellingVal = data.sellingPrice != null && String(data.sellingPrice).trim() !== '' ? Number(data.sellingPrice) : NaN;
    if (!isNaN(retailVal) && !isNaN(sellingVal) && retailVal <= sellingVal) {
      toast.error('Retail price must be greater than selling price');
      return;
    }
    
    setIsSubmitting(true);
    try {
      // Get flavourId from API service or localStorage
      let flavourId = apiService.getFlavourId() || localStorage.getItem('flavourId');
      
      // If no flavourId is set, try to get the first available flavour
      if (!flavourId) {
        try {
          const flavours = await apiService.getAllFlavours();
          if (flavours && flavours.length > 0) {
            flavourId = flavours[0].id.toString();
          } else {
            toast.error('No flavours available. Please create a flavour first.');
            return;
          }
        } catch (error) {
          toast.error('Please select a flavour first');
          return;
        }
      }

      const selectedPlace = places.find(p => p.id === selectedPlaceId);
      const placeName = selectedPlace ? selectedPlace.name : data.place || '';

      const bundleData = {
        ...data,
        flavourId: flavourId,
        tourIds: selectedTourIds,
        thumbnail: thumbnailFile,
        countryId: selectedCountryId || undefined,
        placeId: selectedPlaceId || undefined,
        place: placeName,
      };
      
      await apiService.createBundle(bundleData);

      toast.success('Bundle created successfully!');
      setIsAddModalOpen(false);
      reset();
      setSelectedTourIds([]);
      setSelectedCountryId('');
      setSelectedPlaceId('');
      setThumbnailFile(null);
      await fetchData(currentPage, filterName);
    } catch (error) {
      toast.error('Failed to create bundle');
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmitEdit = async (data: BundleFormData) => {
    if (!selectedBundle) return;
    
    // Validate that if removing existing image, a new image must be uploaded
    if (removeExistingImage && !thumbnailFile) {
      toast.error('Please upload a new image to replace the existing one');
      return;
    }

    // Validate retail price must be greater than selling price when both are provided
    const retailVal = data.retailPrice != null && String(data.retailPrice).trim() !== '' ? parseFloat(String(data.retailPrice)) : NaN;
    const sellingVal = data.sellingPrice != null && String(data.sellingPrice).trim() !== '' ? Number(data.sellingPrice) : NaN;
    if (!isNaN(retailVal) && !isNaN(sellingVal) && retailVal <= sellingVal) {
      toast.error('Retail price must be greater than selling price');
      return;
    }
    
    setIsSubmitting(true);
    try {
      // Get flavourId from API service or localStorage
      let flavourId = apiService.getFlavourId() || localStorage.getItem('flavourId');
      
      // If no flavourId is set, try to get the first available flavour
      if (!flavourId) {
        try {
          const flavours = await apiService.getAllFlavours();
          if (flavours && flavours.length > 0) {
            flavourId = flavours[0].id.toString();
          } else {
            toast.error('No flavours available. Please create a flavour first.');
            return;
          }
        } catch (error) {
          toast.error('Please select a flavour first');
          return;
        }
      }

      const selectedPlace = places.find(p => p.id === selectedPlaceId);
      const placeName = selectedPlace ? selectedPlace.name : data.place || '';

      const bundleData = {
        ...data,
        flavourId: flavourId,
        tourIds: selectedTourIds,
        thumbnail: thumbnailFile,
        removeImage: removeExistingImage,
        countryId: selectedCountryId || undefined,
        placeId: selectedPlaceId || undefined,
        place: placeName,
      };
      
      await apiService.updateBundle(selectedBundle.id, bundleData);
      toast.success('Bundle updated successfully!');
      setIsEditModalOpen(false);
      setSelectedBundle(null);
      reset();
      setSelectedTourIds([]);
      setSelectedCountryId('');
      setSelectedPlaceId('');
      setThumbnailFile(null);
      setRemoveExistingImage(false);
      // Force refresh the data to show updated images
      await fetchData(currentPage, filterName);
    } catch (error) {
      toast.error('Failed to update bundle');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (bundleId: number) => {
    if (!window.confirm('Are you sure you want to delete this bundle? This action cannot be undone.')) {
      return;
    }

    try {
      await apiService.deleteBundle(bundleId);
      toast.success('Bundle deleted successfully!');
      // If we deleted the last item on the page and it's not page 1, go to previous page
      if (bundles.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        await fetchData(currentPage, filterName);
      }
    } catch (error) {
      toast.error('Failed to delete bundle');
    }
  };

  const handleTourToggle = (tourId: number) => {
    setSelectedTourIds(prev => {
      const newIds = prev.includes(tourId)
        ? prev.filter(id => id !== tourId)
        : [...prev, tourId];
      setValue('tourIds', newIds);
      return newIds;
    });
  };

  const handleSelectAllTours = () => {
    const allTourIds = availableTours.map(tour => tour.id);
    setSelectedTourIds(allTourIds);
    setValue('tourIds', allTourIds);
  };

  const handleDeselectAllTours = () => {
    setSelectedTourIds([]);
    setValue('tourIds', []);
  };

  const columns = [
    {
      key: 'name' as keyof Bundle,
      header: 'Bundle Name',
      sortable: true,
      render: (value: any, bundle: Bundle) => (
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center shadow-sm overflow-hidden bg-gray-100">
            {bundle.imageUrl ? (
              <img 
                src={`${bundle.imageUrl}?t=${bundle.updatedAt || Date.now()}`} 
                alt={typeof bundle.name === 'string' ? bundle.name : extractTourText(bundle.name)}
                className="w-full h-full object-cover rounded-lg"
                onError={(e) => {
                  // Fallback to placeholder if image fails to load
                  e.currentTarget.style.display = 'none';
                  const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                  if (nextElement) {
                    nextElement.style.display = 'flex';
                  }
                }}
              />
            ) : null}
            <div className={`w-full h-full bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center ${bundle.imageUrl ? 'hidden' : 'flex'}`}>
            <Package className="h-4 w-4 text-white" />
            </div>
          </div>
          <div className="min-w-0">
            <p className="font-medium text-gray-900 max-w-[200px] truncate">{typeof bundle.name === 'string' ? bundle.name : extractTourText(bundle.name)}</p>
            <p className="text-sm text-gray-500 max-w-[200px] truncate">{bundle.place || 'N/A'}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'description' as keyof Bundle,
      header: 'Description',
      sortable: false,
      render: (value: any, bundle: Bundle) => (
        <p className="text-sm text-gray-900 max-w-[200px] truncate">
          {bundle.description ? (typeof bundle.description === 'string' ? bundle.description : extractTourText(bundle.description)) : 'No description available'}
        </p>
      ),
    },
    {
      key: 'sellingPrice' as keyof Bundle,
      header: 'Price',
      sortable: true,
      render: (value: any, bundle: Bundle) => (
        <div className="text-sm">
          <p className="font-medium text-gray-900">
            ${bundle.sellingPrice?.toFixed(2) || '0.00'}
          </p>
          {bundle.retailPrice && bundle.sellingPrice && parseFloat(bundle.retailPrice) > bundle.sellingPrice && (
            <p className="text-xs text-gray-500 line-through">
              ${parseFloat(bundle.retailPrice).toFixed(2)}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'bundelDetails' as keyof Bundle,
      header: 'Tours',
      sortable: false,
      render: (value: any, bundle: Bundle) => (
        <div className="text-sm">
          <p className="font-medium text-gray-900">
            {bundle.bundelDetails?.length || 0} tours
          </p>
          {bundle.bundelDetails && bundle.bundelDetails.length > 0 && (
            <p className="text-xs text-gray-500 max-w-[200px] truncate">
              {bundle.bundelDetails.slice(0, 2).map(detail => detail.tour?.name ? extractTourText(detail.tour.name) : 'N/A').join(', ')}
              {bundle.bundelDetails.length > 2 && '...'}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'status' as keyof Bundle,
      header: 'Status',
      sortable: true,
      render: (value: any, bundle: Bundle) => (
        <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
          bundle.status === 'active' 
            ? 'bg-green-100 text-green-800' 
            : 'bg-gray-100 text-gray-800'
        }`}>
          {bundle.status}
        </span>
      ),
    },
    {
      key: 'actions' as keyof Bundle,
      header: 'Actions',
      sortable: false,
      render: (value: any, bundle: Bundle) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleView(bundle)}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
            title="View bundle details"
          >
            <Eye className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleEdit(bundle)}
            className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
            title="Edit bundle"
          >
            <Edit className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleDelete(bundle.id)}
            className="p-1 text-gray-400 hover:text-red-600 transition-colors"
            title="Delete bundle"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  // Remove full-page loading state - let the Table component handle loading

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{error}</p>
        <Button onClick={() => fetchData()} className="mt-4">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bundle Management</h1>
          <p className="text-gray-600">Manage tour bundles and packages</p>
        </div>
        <Button onClick={handleAdd} disabled={!apiService.getFlavourId()} title={!apiService.getFlavourId() ? 'Please create an app first' : ''} className="flex items-center space-x-2">
          <Plus className="h-4 w-4" />
          <span>Add Bundle</span>
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name..."
              value={filterNameInput}
              onChange={(e) => setFilterNameInput(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
            />
          </div>
          <div className="flex items-center justify-end">
            <Button onClick={() => fetchData(currentPage, filterName)} variant="outline">
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Bundles Table */}
      <Table
        key={`bundles-${bundles.length}-${bundles.map(b => b.updatedAt).join('-')}`}
        data={sortedBundles}
        columns={columns}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={handleSort}
        loading={isLoading}
        emptyMessage="No bundles found"
      />

      {/* Pagination Controls */}
      {totalBundles > 0 && (
        <div className="flex justify-between items-center mt-4">
          <div className="text-sm text-gray-600">
            {totalPages > 0 && ` Page ${currentPage} of ${totalPages}`}
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              disabled={currentPage === 1 || isLoading}
              onClick={() => {
                const newPage = currentPage - 1;
                setCurrentPage(newPage);
              }}
            >
              Previous
            </Button>

            <Button
              variant="outline"
              disabled={currentPage >= totalPages || isLoading}
              onClick={() => {
                const newPage = currentPage + 1;
                setCurrentPage(newPage);
              }}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* View Bundle Modal */}
      <Modal
        isOpen={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setSelectedBundle(null);
        }}
        title="Bundle Details"
        size="lg"
      >
        {selectedBundle && (
          <div className="space-y-6">
            {/* Bundle Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Bundle Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <p className="text-sm text-gray-900 mt-1">{typeof selectedBundle.name === 'string' ? selectedBundle.name : extractTourText(selectedBundle.name)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Place</label>
                  <p className="text-sm text-gray-900 mt-1">{selectedBundle.place || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <p className="text-sm text-gray-900 mt-1">{selectedBundle.status}</p>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <p className="text-sm text-gray-900 mt-1">{selectedBundle.description ? (typeof selectedBundle.description === 'string' ? selectedBundle.description : extractTourText(selectedBundle.description)) : 'No description available'}</p>
                </div>
              </div>
            </div>


            {/* Pricing Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">💰 Pricing Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Selling Price</label>
                  <p className="text-sm text-gray-900 mt-1 font-semibold text-lg">${selectedBundle.sellingPrice?.toFixed(2) || '0.00'}</p>
                </div>
                {selectedBundle.retailPrice && selectedBundle.sellingPrice && parseFloat(selectedBundle.retailPrice) > selectedBundle.sellingPrice && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Retail Price</label>
                    <p className="text-sm text-gray-500 mt-1 line-through">${parseFloat(selectedBundle.retailPrice).toFixed(2)}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Included Tours */}
            {selectedBundle.bundelDetails && selectedBundle.bundelDetails.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">🎯 Included Tours ({selectedBundle.bundelDetails.length})</h3>
                <div className="space-y-2">
                  {selectedBundle.bundelDetails.map((detail, index) => (
                    <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                        <MapPin className="h-4 w-4 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{detail.tour?.name ? extractTourText(detail.tour.name) : `Tour ${detail.tourId}`}</p>
                        <p className="text-sm text-gray-500">{detail.tour?.description ? extractTourText(detail.tour.description) : 'No description'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex space-x-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setIsViewModalOpen(false);
                  setSelectedBundle(null);
                }}
              >
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Add/Edit Bundle Modal */}
      <Modal
        isOpen={isAddModalOpen || isEditModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setIsEditModalOpen(false);
          setSelectedBundle(null);
          setSelectedTourIds([]);
          setSelectedCountryId('');
          setSelectedPlaceId('');
          setShowAddCountryModal(false);
          setShowAddPlaceModal(false);
          reset();
        }}
        title={isEditModalOpen ? 'Edit Bundle' : 'Add New Bundle'}
        size="xl"
      >
        <form 
          onSubmit={handleSubmit(isEditModalOpen ? onSubmitEdit : onSubmitAdd)} 
          className="space-y-6"
        >
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
            
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bundle Name *
              </label>
              <input
                {...register('name', { required: 'Bundle name is required' })}
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                placeholder="Enter bundle name"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description *
            </label>
            <textarea
              {...register('description', { required: 'Description is required' })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
              placeholder="Enter bundle description"
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
            )}
          </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Country
                </label>
                <Select
                  value={selectedCountryId ? countryOptions.find(opt => opt.value === selectedCountryId) : null}
                  onChange={handleCountryChange}
                  options={countryOptions}
                  placeholder="Search or select country..."
                  isSearchable
                  isClearable
                  components={{ Option }}
                  styles={selectStyles}
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location
                </label>
                <Select
                  value={selectedPlaceId ? placeOptions.find(opt => opt.value === selectedPlaceId) : null}
                  onChange={handlePlaceChange}
                  options={placeOptions}
                  placeholder={selectedCountryId ? "Search or select place..." : "Please select a country first"}
                  isSearchable
                  isClearable
                  isDisabled={!selectedCountryId}
                  components={{ Option }}
                  styles={{
                    ...selectStyles,
                    control: (base: CSSObjectWithLabel, state: ControlProps<OptionType, false>) => ({
                      ...base,
                      borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
                      boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
                      backgroundColor: state.isDisabled ? '#f3f4f6' : 'white',
                      cursor: state.isDisabled ? 'not-allowed' : 'default',
                      '&:hover': {
                        borderColor: state.isFocused ? '#3b82f6' : state.isDisabled ? '#d1d5db' : '#9ca3af',
                      },
                    }),
                  }}
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
                {!selectedCountryId && (
                  <p className="text-xs text-gray-500 mt-1">
                    Please select a country first
                  </p>
                )}
              </div>
            </div>

            {/* <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bundle Email Template
              </label>
              <textarea
                {...register('bundleEmailTemplate')}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                placeholder="Enter email template for bundle notifications"
              />
            </div> */}
          </div>


          {/* Product Identifiers */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Product Identifiers</h3>
            
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product ID
              </label>
              <input
                  {...register('productId')}
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                  placeholder="BUNDLE001"
              />
            </div>

            {/* <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bokun ID
              </label>
              <input
                  {...register('bokunId')}
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                  placeholder="BOKUN001"
              />
            </div> */}
          </div>

          </div>

          {/* Thumbnail Image */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Thumbnail Image</h3>

          <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bundle Thumbnail
            </label>
              
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-gray-400 transition-colors">
                <div className="space-y-1 text-center">
                  {thumbnailFile ? (
                    <div className="space-y-2">
                      <img
                        src={URL.createObjectURL(thumbnailFile)}
                        alt="Thumbnail preview"
                        className="mx-auto h-32 w-32 object-cover rounded-md"
                      />
                      <p className="text-sm text-gray-600">{thumbnailFile.name}</p>
                      <button
                        type="button"
                        onClick={() => setThumbnailFile(null)}
                        className="text-sm text-red-600 hover:text-red-800"
                      >
                        Remove
                      </button>
                    </div>
                  ) : selectedBundle?.imageUrl && !removeExistingImage ? (
                    <div className="space-y-2">
                      <img
                        src={`${selectedBundle.imageUrl}?t=${selectedBundle.updatedAt || Date.now()}`}
                        alt="Current thumbnail"
                        className="mx-auto h-32 w-32 object-cover rounded-md"
                      />
                      <p className="text-sm text-gray-600">Current thumbnail</p>
                      <button
                        type="button"
                        onClick={() => {
                          setRemoveExistingImage(true);
                          setThumbnailFile(null);
                        }}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        Replace Image
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Upload className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="flex text-sm text-gray-600">
                        <label
                          htmlFor="thumbnail-upload"
                          className="relative cursor-pointer bg-white rounded-md font-medium text-primary hover:text-primary focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary"
                        >
                          <span>Upload thumbnail image *</span>
                          <input
                            id="thumbnail-upload"
                            name="thumbnail-upload"
                            type="file"
                            className="sr-only"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                setThumbnailFile(file);
                              }
                            }}
                          />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Pricing Information</h3>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Selling Price *
              </label>
              <input
                {...register('sellingPrice', { 
                  required: 'Selling price is required',
                  min: { value: 0, message: 'Price must be positive' }
                })}
                type="number"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                placeholder="0.00"
              />
              {errors.sellingPrice && (
                <p className="mt-1 text-sm text-red-600">{errors.sellingPrice.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Retail Price
              </label>
              <input
                {...register('retailPrice', { 
                  min: { value: 0, message: 'Price must be positive' }
                })}
                type="number"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                placeholder="0.00"
              />
              {errors.retailPrice && (
                <p className="mt-1 text-sm text-red-600">{errors.retailPrice.message}</p>
              )}
            </div>
          </div>
          </div>



          {/* Tour Selection */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Include Tours in Bundle</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Tours to Include *
              </label>
              
              {availableTours.length === 0 ? (
                <div className="p-4 border border-gray-300 rounded-md bg-gray-50 text-center">
                  <Package className="h-6 w-6 mx-auto text-gray-400 mb-2" />
                  <p className="text-gray-500 text-sm">
                    No tours available for this flavour. Create some tours first.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Tours List */}
                  <div
                    ref={tourListRef}
                    onScroll={handleTourListScroll}
                    className="border border-gray-300 rounded-md p-3 bg-white space-y-2 overflow-y-auto"
                    style={{ maxHeight: '320px' }}
                  >
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-sm font-medium text-gray-700">
                        Available Tours ({availableTours.length}{tourTotalPages > 1 ? '+' : ''})
                      </span>
                      <div className="space-x-2">
                        <button
                          type="button"
                          onClick={handleSelectAllTours}
                          className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          Select All
                        </button>
                        <button
                          type="button"
                          onClick={handleDeselectAllTours}
                          className="text-xs px-2 py-1 bg-gray-500 text-white rounded hover:bg-gray-600"
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                    
                    {availableTours.map(tour => (
                      <label
                        key={tour.id}
                        className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedTourIds.includes(tour.id)}
                          onChange={() => handleTourToggle(tour.id)}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <MapPin className="h-4 w-4 text-gray-400" />
                            <span className="font-medium text-gray-900">{extractTourText(tour.name)}</span>
                          </div>
                          {tour.place && (
                            <p className="text-xs text-gray-500 ml-6">📍 {tour.place}</p>
                          )}
                          {tour.description && (
                            <p className="text-xs text-gray-500 ml-6 truncate">{extractTourText(tour.description)}</p>
                          )}
                        </div>
                      </label>
                    ))}
                    {isLoadingMoreTours && (
                      <div className="flex justify-center py-2">
                        <span className="text-sm text-gray-500">Loading more tours...</span>
                      </div>
                    )}
                    {!isLoadingMoreTours && tourPage >= tourTotalPages && tourTotalPages > 1 && (
                      <p className="text-xs text-center text-gray-400 py-1">All tours loaded</p>
                    )}
                  </div>
                  
                  {/* Selected Summary */}
                  {selectedTourIds.length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                      <p className="text-sm font-medium text-blue-900 mb-2">
                        Selected: {selectedTourIds.length} tour(s)
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {selectedTourIds.map(tourId => {
                          const tour = availableTours.find(t => t.id === tourId);
                          return tour ? (
                            <span 
                              key={tourId} 
                              className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded"
                            >
                              {extractTourText(tour.name)}
                              <button
                                type="button"
                                onClick={() => handleTourToggle(tour.id)}
                                className="ml-1 text-blue-600 hover:text-blue-800"
                              >
                                ×
                              </button>
                            </span>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Status */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Bundle Status</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                {...register('status')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
              >
                <option value="">Select status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div className="flex space-x-3 pt-4 border-t">
            <Button type="submit" className="flex-1" disabled={isSubmitting}>
              {isSubmitting ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {isEditModalOpen ? 'Updating...' : 'Creating...'}
                </div>
              ) : (
                isEditModalOpen ? 'Update Bundle' : 'Create Bundle'
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={isSubmitting}
              onClick={() => {
                setIsAddModalOpen(false);
                setIsEditModalOpen(false);
                setSelectedBundle(null);
                setSelectedTourIds([]);
                setSelectedCountryId('');
                setSelectedPlaceId('');
                setThumbnailFile(null);
                reset();
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Modal>

      {showAddCountryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add New Country</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Country Name *</label>
              <input
                type="text"
                value={newCountryName}
                onChange={(e) => setNewCountryName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                placeholder="Enter country name"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                type="button"
                onClick={() => {
                  setShowAddCountryModal(false);
                  setNewCountryName('');
                }}
                disabled={isCreatingCountry}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleCreateCountry}
                disabled={isCreatingCountry || !newCountryName.trim()}
              >
                {isCreatingCountry ? 'Creating...' : 'Create'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showAddPlaceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add New Place</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Place Name *</label>
              <input
                type="text"
                value={newPlaceName}
                onChange={(e) => setNewPlaceName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                placeholder="Enter place name"
              />
              <p className="text-xs text-gray-500 mt-1">
                Country: {countries.find(c => c.id === selectedCountryId)?.name || 'N/A'}
              </p>
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                type="button"
                onClick={() => {
                  setShowAddPlaceModal(false);
                  setNewPlaceName('');
                }}
                disabled={isCreatingPlace}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleCreatePlace}
                disabled={isCreatingPlace || !newPlaceName.trim()}
              >
                {isCreatingPlace ? 'Creating...' : 'Create'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Bundles; 